import { ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Component } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NavigationEnd, NavigationStart } from '@angular/router';
import { AvailableLangs, TranslocoService } from '@jsverse/transloco';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { Store } from '@ngrx/store';
import { selectSelectedOrganizationId } from 'shared/store/user-info/user-info.selector';
import { SnackbarService } from 'services/snackbar/snackbar.service';
import { UserPrivilege } from '../../features/main-page/types';
import { Entity, Permission, privilege } from '../../features/main-page/types';

export enum NavigationPoints {
  NSC = 'nsc',
  ORGANIZATION = 'organization',
  VEHICLE_ADMIN = 'vehicle-admin',
  DEAL_ADMIN = 'deal-admin',
  BANK_ADMIN = 'bank-admin',
  DEALER_ADMIN = 'dealer-admin',
  ACTIVITY_ADMIN = 'activities-admin',
  LIST_OF_VALUES = 'list-of-values',
  USERS = 'users',
  FILES = 'files',
  LOGIC_PROCESS = 'logic-process',
  MAPPING = 'mapping',
  TDPP_REPORT_DATA = 'tdpp-report-data-admin',
}

export const permissions: Record<NavigationPoints, string[]> = {
  [NavigationPoints.ORGANIZATION]: [
    Entity.ORGANIZATION,
    Entity.FEATURE_PARAMETER,
    Entity.FINANCE_PRODUCT_MAPPING,
    Entity.PRODUCT_LINE_PARAMETER,
  ],
  [NavigationPoints.NSC]: [
    Entity.ORGANIZATION,
    Entity.FEATURE_PARAMETER,
    Entity.FINANCE_PRODUCT_MAPPING,
    Entity.PRODUCT_LINE_PARAMETER,
  ],
  [NavigationPoints.VEHICLE_ADMIN]: [
    Entity.VEHICLE,
    Entity.PRODUCT,
    Entity.PRODUCT_LINE,
    Entity.OPTION_PRICE,
    Entity.PRICE,
    Entity.VEHICLE_MAPPING,
    Entity.USED_VALUE_MATRIX,
    Entity.USED_VALUE,
  ],
  [NavigationPoints.DEAL_ADMIN]: [Entity.DEAL],
  [NavigationPoints.BANK_ADMIN]: [
    Entity.BANK,
    Entity.BANK_CONTRACT,
    Entity.CALENDAR_RULE,
    Entity.EXTERNAL_API_USER_DATA,
  ],
  [NavigationPoints.DEALER_ADMIN]: [
    Entity.DEALER,
    Entity.DEALER_PARAMETER_CONTROL,
    Entity.DEALER_PARAMETER_ADJUSTMENT,
    Entity.DEALER_STOCK,
  ],
  [NavigationPoints.ACTIVITY_ADMIN]: [
    Entity.ACTIVITY,
    Entity.ACTIVITY_MATRIX,
    Entity.OPPORTUNITY,
  ],
  [NavigationPoints.LIST_OF_VALUES]: [Entity.LIST_OF_VALUE],
  [NavigationPoints.USERS]: [Entity.USER],
  [NavigationPoints.FILES]: [Entity.FILE],
  [NavigationPoints.LOGIC_PROCESS]: [Entity.LOGIC_PROCESS],
  [NavigationPoints.MAPPING]: [Entity.MAPPING],
  [NavigationPoints.TDPP_REPORT_DATA]: [Entity.TDPP_REPORT_DATA],
};

@Component({
  selector: 'app-main',
  standalone: false,
  templateUrl: './main-app-layout.component.html',
  styleUrl: './main-app-layout.component.css',
})
export class MainAppLayoutComponent implements OnInit {
  languages = [] as AvailableLangs;
  currentRoute = '';

  selectedOrganization$ = this.store.select(selectSelectedOrganizationId);

  constructor(
    private readonly translationService: TranslocoService,
    private readonly localeService: TranslocoLocaleService,
    public router: Router,
    public route: ActivatedRoute,
    private readonly dateAdapter: DateAdapter<Date>,
    private readonly store: Store,
    private readonly snackbarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.languages = this.translationService.getAvailableLangs();

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.highlightCurrentRoute();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      if (event instanceof NavigationStart) {
        this.snackbarService.close();
      }
    });
  }

  @ViewChildren('navbarItem') navbarItems!: QueryList<ElementRef>;

  highlightCurrentRoute() {
    this.currentRoute = this.router.routerState.snapshot.url;

    for (const item of this.navbarItems) {
      const element = item.nativeElement;
      const itemRoute = element.getAttribute('routerLink');
      const labelSpan = element.querySelector('.mdc-button__label > span');

      if (labelSpan) {
        if (itemRoute && this.currentRoute.split('?')[0] === itemRoute) {
          labelSpan.classList.add('active');
        } else {
          labelSpan.classList.remove('active');
        }
      }
    }
  }

  userPrivileges = Object.fromEntries(
    Object.entries(permissions).map(([key, entities]) => [
      key,
      entities.map((entity) => `${entity}_READ` as UserPrivilege),
    ]),
  ) as Record<NavigationPoints, UserPrivilege[]>;

  protected readonly Entity = Entity;
  protected readonly NavigationPoints = NavigationPoints;
  protected readonly privilege = privilege;
  protected readonly Permissions = Permission;
}
